import { translate, translateRich, useLocale } from "../../localization";
import React, { useMemo } from "react";
import "./WeekOverWeekComparison.scss";

/**
 * WeekOverWeekComparison
 * 
 * Shows simple, trustworthy comparison between this week and last week.
 * No fancy charts - just clear numbers and directional indicators.
 * 
 * Decision it enables: "Are things improving or getting worse?"
 */
export default function WeekOverWeekComparison({ sessions = [] }) {
  const locale = useLocale();
  
  const comparison = useMemo(() => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

    // Split sessions into this week and last week
    const thisWeekSessions = sessions.filter(s => {
      const date = new Date(s.startTime || s.timestamp);
      return date >= oneWeekAgo && date <= now;
    });

    const lastWeekSessions = sessions.filter(s => {
      const date = new Date(s.startTime || s.timestamp);
      return date >= twoWeeksAgo && date < oneWeekAgo;
    });

    // Calculate metrics for each period
    const calcMetrics = (sessionList) => {
      if (sessionList.length === 0) {
        return { sessions: 0, avgScore: null, avgTime: null, responses: 0 };
      }

      const totalScore = sessionList.reduce((sum, s) => sum + (s.meanScore || 0), 0);
      const totalTime = sessionList.reduce((sum, s) => sum + (s.secondsSpent || 0), 0);
      const totalResponses = sessionList.reduce((sum, s) => sum + (s.responseCount || 0), 0);
      const scoredSessions = sessionList.filter(s => s.meanScore != null).length;

      return {
        sessions: sessionList.length,
        avgScore: scoredSessions > 0 ? Math.round(totalScore / scoredSessions) : null,
        avgTime: sessionList.length > 0 ? Math.round(totalTime / sessionList.length) : null,
        responses: totalResponses,
      };
    };

    const thisWeek = calcMetrics(thisWeekSessions);
    const lastWeek = calcMetrics(lastWeekSessions);

    // Calculate changes
    const calcChange = (current, previous) => {
      if (previous === null || previous === 0) {
        if (current === null || current === 0) return { value: 0, direction: "same" };
        return { value: 100, direction: "up" };
      }
      if (current === null) return { value: -100, direction: "down" };
      
      const change = Math.round(((current - previous) / previous) * 100);
      return {
        value: Math.abs(change),
        direction: change > 0 ? "up" : change < 0 ? "down" : "same"
      };
    };

    return {
      thisWeek,
      lastWeek,
      changes: {
        sessions: calcChange(thisWeek.sessions, lastWeek.sessions),
        avgScore: calcChange(thisWeek.avgScore, lastWeek.avgScore),
        avgTime: calcChange(thisWeek.avgTime, lastWeek.avgTime),
        responses: calcChange(thisWeek.responses, lastWeek.responses),
      },
      hasLastWeekData: lastWeek.sessions > 0,
    };
  }, [sessions, locale]);

  const formatTime = (seconds) => {
    if (seconds === null) return "—";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getChangeClass = (change, isPositiveGood = true) => {
    if (change.direction === "same") return "same";
    if (change.direction === "up") return isPositiveGood ? "positive" : "negative";
    return isPositiveGood ? "negative" : "positive";
  };

  const getChangeArrow = (direction) => {
    if (direction === "up") return "↑";
    if (direction === "down") return "↓";
    return "—";
  };

  // If no data at all
  if (comparison.thisWeek.sessions === 0 && comparison.lastWeek.sessions === 0) {
    return (
      <div className="weekComparison">
        <div className="wcEmpty">
          <div className="wcEmptyIcon">📊</div>
          <div className="wcEmptyText">{translate("Not enough data yet")}</div>
          <div className="wcEmptySub">{translate("Check back after a week of activity")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="weekComparison">
      {/* Period labels */}
      <div className="wcHeader">
        <div className="wcPeriod">{translateRich("{value0}This Week", {
  value0: <span className="wcPeriodDot wcPeriodDot--current" />
})}</div>
        {comparison.hasLastWeekData && (
          <div className="wcPeriod wcPeriod--muted">{translateRich("{value0}vs Last Week", {
  value0: <span className="wcPeriodDot wcPeriodDot--previous" />
})}</div>
        )}
      </div>

      {/* Metrics grid */}
      <div className="wcMetrics">
        {/* Sessions */}
        <div className="wcMetric">
          <div className="wcMetricLabel">{translate("Sessions")}</div>
          <div className="wcMetricValue">{comparison.thisWeek.sessions}</div>
          {comparison.hasLastWeekData && (
            <div className={`wcChange wcChange--${getChangeClass(comparison.changes.sessions)}`}>
              <span className="wcChangeArrow">{getChangeArrow(comparison.changes.sessions.direction)}</span>
              <span className="wcChangeValue">{comparison.changes.sessions.value}%</span>
            </div>
          )}
          {comparison.hasLastWeekData && (
            <div className="wcPrevious">{translateRich("was {value0}", {
  value0: comparison.lastWeek.sessions
})}</div>
          )}
        </div>

        {/* Avg Score */}
        <div className="wcMetric">
          <div className="wcMetricLabel">{translate("Avg Score")}</div>
          <div className="wcMetricValue">
            {comparison.thisWeek.avgScore !== null ? `${comparison.thisWeek.avgScore}%` : "—"}
          </div>
          {comparison.hasLastWeekData && comparison.thisWeek.avgScore !== null && (
            <div className={`wcChange wcChange--${getChangeClass(comparison.changes.avgScore)}`}>
              <span className="wcChangeArrow">{getChangeArrow(comparison.changes.avgScore.direction)}</span>
              <span className="wcChangeValue">{comparison.changes.avgScore.value}%</span>
            </div>
          )}
          {comparison.hasLastWeekData && comparison.lastWeek.avgScore !== null && (
            <div className="wcPrevious">{translateRich("was {value0}%", {
  value0: comparison.lastWeek.avgScore
})}</div>
          )}
        </div>

        {/* Avg Time */}
        <div className="wcMetric">
          <div className="wcMetricLabel">{translate("Avg Time")}</div>
          <div className="wcMetricValue">{formatTime(comparison.thisWeek.avgTime)}</div>
          {comparison.hasLastWeekData && comparison.thisWeek.avgTime !== null && (
            <div className={`wcChange wcChange--${getChangeClass(comparison.changes.avgTime)}`}>
              <span className="wcChangeArrow">{getChangeArrow(comparison.changes.avgTime.direction)}</span>
              <span className="wcChangeValue">{comparison.changes.avgTime.value}%</span>
            </div>
          )}
          {comparison.hasLastWeekData && comparison.lastWeek.avgTime !== null && (
            <div className="wcPrevious">{translateRich("was {value0}", {
  value0: formatTime(comparison.lastWeek.avgTime)
})}</div>
          )}
        </div>

        {/* Total Responses */}
        <div className="wcMetric">
          <div className="wcMetricLabel">{translate("Responses")}</div>
          <div className="wcMetricValue">{comparison.thisWeek.responses}</div>
          {comparison.hasLastWeekData && (
            <div className={`wcChange wcChange--${getChangeClass(comparison.changes.responses)}`}>
              <span className="wcChangeArrow">{getChangeArrow(comparison.changes.responses.direction)}</span>
              <span className="wcChangeValue">{comparison.changes.responses.value}%</span>
            </div>
          )}
          {comparison.hasLastWeekData && (
            <div className="wcPrevious">{translateRich("was {value0}", {
  value0: comparison.lastWeek.responses
})}</div>
          )}
        </div>
      </div>

      {/* Insight summary */}
      {comparison.hasLastWeekData && (
        <div className="wcInsight">
          {comparison.changes.avgScore.direction === "up" && comparison.changes.avgScore.value >= 5 && (
            <span className="wcInsightText wcInsightText--positive">{translate("\uD83D\uDCC8 Scores improving! Keep up the momentum.")}</span>
          )}
          {comparison.changes.avgScore.direction === "down" && comparison.changes.avgScore.value >= 10 && (
            <span className="wcInsightText wcInsightText--negative">{translate("\uD83D\uDCC9 Scores dropped this week. Consider reviewing difficult questions.")}</span>
          )}
          {comparison.changes.sessions.direction === "up" && comparison.changes.sessions.value >= 20 && (
            <span className="wcInsightText wcInsightText--positive">{translateRich("\uD83C\uDF89 Great engagement! {value0}% more sessions than last week.", {
  value0: comparison.changes.sessions.value
})}</span>
          )}
          {comparison.changes.sessions.direction === "down" && comparison.changes.sessions.value >= 30 && (
            <span className="wcInsightText wcInsightText--warning">{translate("\u26A0\uFE0F Participation dropped significantly. Consider re-engaging students.")}</span>
          )}
        </div>
      )}

      {!comparison.hasLastWeekData && (
        <div className="wcNoHistory">
          <span className="wcNoHistoryText">{translate("No data from last week to compare. Check back next week for trends!")}</span>
        </div>
      )}
    </div>
  );
}
